import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {CONTROLLER_DATA, MIN_MAX_CONTROLLER} from '../Model/Models';
import HandleChange from '../../../../../Helpers/HandleChange';
import { isIpFormat } from "../../../../../Apis/functions";
import {useTranslation} from "react-i18next";
import { GetListStatus } from '../../../../../reduxToolkit/Commons/CommonsApi';

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

/**
 * CommonControllerApp is a React hook that manages the logic for handling controller data,
 * validation, and status retrieval. It interacts with Redux to fetch device statuses and
 * provides input validation for IP addresses and offset values.
 *
 * @returns {object} An object containing controller data, validation states, and handler functions.
 */
const CommonControllerApp = () => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const { getListStatus } = useSelector(state => state.CommonsSlice);
    const [emptyFields, setEmptyFields] = useState(false);
    const [isValidIp, setIsValidIp] = useState(true);
    const [isValidOffset, setIsValidOffset] = useState(true);
    const [controller, setController] = useState({ ...CONTROLLER_DATA });
    const { handleChange } = HandleChange({ model: controller, setModel: setController });

    /**
     * Fetches all available device statuses when the component mounts.
     */
    useEffect(() => {
        dispatch(GetListStatus());
    }, []);

    /**
     * Validates the IP address format whenever it changes.
     * Updates the isValidIp state accordingly.
     */
    useEffect(() => {
        if (controller?.ipAddress !== null && controller?.ipAddress !== "")
            setIsValidIp(isIpFormat(controller?.ipAddress));
    }, [controller?.ipAddress]);

    /**
     * Validates the offset value.
     * The offset must be a number greater than or equal to 100.
     * Updates the isValidOffset state accordingly.
     */
    useEffect(() => {
        if (controller.offset) {
            const value = parseInt(controller.offset);
            if (!isNaN(value) && value >= MIN_MAX_CONTROLLER.OFFSET.start) {
                setIsValidOffset(true);
            } else {
                setIsValidOffset(false);
            }
        } else {
            setIsValidOffset(true);
        }
    }, [controller.offset]);

    /**
     * Resets all controller fields to their default values and clears validation states.
     *
     * @function
     */
    const handleClear = () => {
        setController({ ...CONTROLLER_DATA });
        setEmptyFields(false);
    }

    return {
        t,
        controller,
        setController,
        handleChange,
        emptyFields,
        setEmptyFields,
        getListStatus,
        isValidIp,
        isValidOffset,
        handleClear
    }

}

export default CommonControllerApp;
